import { createTheme } from '@mui/material/styles';

/**
 * Lienion AI Design System
 * 
 * This file centralizes all design tokens and theme configuration following ATOMic design principles.
 * All components are built using these foundational tokens.
 */

// ======================================================
// DESIGN TOKENS (ATOMS)
// These are the foundational building blocks of our design system
// ======================================================

/**
 * Color Palette
 * These are the raw color values that form the foundation of our design system
 */
const palette = {
  // Primary colors
  primary: {
    main: '#485869', // Slate Blue-Gray
    light: '#6c7c8c',
    dark: '#3A4754',
    contrastText: '#FFFFFF',
  },
  // Secondary colors
  secondary: {
    main: '#485869', // Slate Blue-Gray
    light: '#6c7c8c',
    dark: '#3A4754',
    contrastText: '#FFFFFF',
  },
  // Functional area colors
  functional: {
    shouton: {
      main: '#CED015', // Bird Flower
      dark: '#999900', // Olive
    },
    todosion: {
      main: '#66CC33', // Atlantis
      dark: '#359946', // Sea Green
    },
    tempion: {
      main: '#7BCCCC', // Monte Carlo
      dark: '#349999', // Lochinvar
    },
    releasion: {
      main: '#4DBCF3', // Picton Blue
      dark: '#2C85B1', // Jelly Bean
    },
    essencion: {
      main: '#F54D80', // French Rose
      dark: '#D75274', // Cranberry
    },
    testcasion: {
      main: '#9966CC', // Amethyst
      dark: '#7550A0', // Butterfly Bush
    },
    trenion: {
      main: '#fa5b5b', // Carnation
      dark: '#fa2424', // Torch Red
    },
    sequencion: {
      main: '#FF9933', // Neon Carrot
      dark: '#DC7321', // Hot Cinnamon
    },
  },
  // System feedback colors
  success: {
    main: '#66CC33', // Atlantis
    light: '#7DE050',
    dark: '#359946',
    contrastText: '#FFFFFF',
  },
  warning: {
    main: '#FF9933', // Neon Carrot
    light: '#FFAC5A',
    dark: '#DC7321',
    contrastText: '#FFFFFF',
  },
  error: {
    main: '#fa5b5b', // Carnation
    light: '#FF7A7A',
    dark: '#fa2424',
    contrastText: '#FFFFFF',
  },
  info: {
    main: '#4DBCF3', // Picton Blue
    light: '#71CDFF',
    dark: '#2C85B1',
    contrastText: '#FFFFFF',
  },
  // Neutral colors
  grey: {
    50: '#F5F6F7', // Cloud White
    100: '#E3E6E8',
    200: '#C8CDD2', // Cloud Gray
    300: '#A2ABB3',
    400: '#8D979F',
    500: '#6B757F',
    600: '#57616B',
    700: '#485869', // Slate Blue-Gray
    800: '#3A4754',
    900: '#2B3640',
  },
  // Text colors
  text: {
    primary: '#485869', // Slate Blue-Gray
    secondary: '#6B757F',
    disabled: '#A2ABB3',
  },
  // Logo colors
  logo: {
    yellow: '#FFDD0A', // Sunshine Yellow
    green: '#72BF33', // Leaf Green
    emerald: '#21A664', // Emerald Green
    azure: '#5CB9E5', // Bright Azure
    purple: '#8769BF', // Amethyst Purple
    orange: '#FF694C', // Flamingo Orange
    marigold: '#FFAC08', // Bright Marigold
  },
  // Tertiary colors
  tertiary: {
    texas: '#F2F78D',
    watermelon: '#FF6E78',
    aquamarine: '#7CF7C0',
    matisse: '#1F78B4',
    salem: '#09932E',
  },
  background: {
    default: '#F5F6F7', // Cloud White
    paper: '#FFFFFF',
    light: '#FAFAFA',
    dark: '#E3E6E8',
  },
  divider: 'rgba(0, 0, 0, 0.12)',
};

/**
 * Typography
 * Font family, sizes, weights, and other typographic settings
 */
const typography = {
  fontFamily: '"Sofia Pro Regular", "Sofia Pro", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  fontWeightLight: 300,
  fontWeightRegular: 400,
  fontWeightMedium: 500,
  fontWeightSemiBold: 600,
  fontWeightBold: 700,
  h1: {
    fontFamily: '"Sofia Pro Bold", "Sofia Pro", sans-serif',
    fontSize: '1.75rem', // 28px
    fontWeight: 700, // Bold
    lineHeight: 1.2,
  },
  h2: {
    fontFamily: '"Sofia Pro Bold", "Sofia Pro", sans-serif',
    fontSize: '1.625rem', // 26px
    fontWeight: 700, // Bold
    lineHeight: 1.2,
  },
  h3: {
    fontFamily: '"Sofia Pro Bold", "Sofia Pro", sans-serif',
    fontSize: '1.5rem', // 24px
    fontWeight: 700, // Bold
    lineHeight: 1.3,
  },
  h4: {
    fontFamily: '"Sofia Pro Bold", "Sofia Pro", sans-serif',
    fontSize: '1.25rem', // 20px
    fontWeight: 700, // Bold
    lineHeight: 1.3,
  },
  h5: {
    fontFamily: '"Sofia Pro Bold", "Sofia Pro", sans-serif',
    fontSize: '1.125rem', // 18px
    fontWeight: 700, // Bold
    lineHeight: 1.4,
  },
  h6: {
    fontFamily: '"Sofia Pro Bold", "Sofia Pro", sans-serif',
    fontSize: '1rem', // 16px
    fontWeight: 700, // Bold
    lineHeight: 1.4,
  },
  subtitle1: {
    fontSize: '1.25rem', // 20px
    fontWeight: 500, // Medium
    lineHeight: 1.5,
  },
  subtitle2: {
    fontSize: '1.125rem', // 18px
    fontWeight: 400, // Regular
    lineHeight: 1.5,
  },
  body1: {
    fontFamily: '"Sofia Pro Regular", "Sofia Pro", sans-serif',
    fontSize: '1rem', // 16px
    fontWeight: 400, // Regular
    lineHeight: 1.5,
  },
  body2: {
    fontFamily: '"Sofia Pro Regular", "Sofia Pro", sans-serif',
    fontSize: '0.875rem', // 14px
    fontWeight: 400, // Regular
    lineHeight: 1.5,
  },
  button: {
    fontFamily: '"Sofia Pro Regular", "Sofia Pro", sans-serif',
    fontSize: '1rem', // 16px
    fontWeight: 400, // Regular
    textTransform: 'none',
  },
  caption: {
    fontFamily: '"Sofia Pro Regular", "Sofia Pro", sans-serif',
    fontSize: '0.875rem', // 14px
    lineHeight: 1.5,
    fontWeight: 400, // Regular
  },
  overline: {
    fontFamily: '"Sofia Pro Regular", "Sofia Pro", sans-serif',
    fontSize: '0.75rem',
    lineHeight: 1.5,
    textTransform: 'uppercase',
    fontWeight: 400,
    letterSpacing: '0.05em',
  },
};

/**
 * Spacing
 * Base spacing unit and increments
 */
const spacing = 4; // Base spacing unit of 4px

/**
 * Breakpoints
 * Screen size breakpoints for responsive design
 */
const breakpoints = {
  values: {
    xs: 0,
    sm: 600,
    md: 960,
    lg: 1280,
    xl: 1920,
  },
};

/**
 * Shape
 * Border radius and other shape-related properties
 */
const shape = {
  borderRadius: 4, // 4px base border radius
  borderRadiusMedium: 8, // 8px medium border radius
  borderRadiusLarge: 16, // 16px large border radius
};

/**
 * Shadows
 * Box shadows for elevation
 */
const shadows = [
  'none',
  '0px 1px 2px rgba(0, 0, 0, 0.05)', // Subtle shadow for slight elevation
  '0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.06)', // Light shadow for cards
  '0px 4px 6px -1px rgba(0, 0, 0, 0.1), 0px 2px 4px -1px rgba(0, 0, 0, 0.06)', // Medium shadow for dropdowns
  '0px 10px 15px -3px rgba(0, 0, 0, 0.1), 0px 4px 6px -2px rgba(0, 0, 0, 0.05)', // Strong shadow for modals
  '0px 20px 25px -5px rgba(0, 0, 0, 0.1), 0px 10px 10px -5px rgba(0, 0, 0, 0.04)', // Heavy shadow for popovers
  '0px 25px 50px -12px rgba(0, 0, 0, 0.25)', // Intense shadow for emphasis
  // Rest of shadows array for MUI compatibility
  ...Array(18).fill('0px 25px 50px -12px rgba(0, 0, 0, 0.25)'),
];

// ======================================================
// COMPONENT STYLES (MOLECULES)
// Component-specific styles that use the design tokens
// ======================================================

/**
 * Component Styles
 * Styling for MUI components using our design tokens
 */
const components = {
  // Button styling
  MuiButton: {
    styleOverrides: {
      root: {
        borderRadius: '0.25rem', // 4px border radius per design guidelines
        padding: '0.5rem 1.5rem',
        minWidth: '7.063rem', // 113px
        height: '2.813rem', // 45px
        textTransform: 'capitalize', // Only capitalize first letter
        fontWeight: 400,
        fontSize: '1rem', // 16px
        boxShadow: 'none',
        border: 'none',
        '&:hover': {
          boxShadow: 'none', // No shadow even on hover
        },
      },
      // Primary contained button (slate blue-gray)
      containedPrimary: {
        backgroundColor: palette.primary.main,
        color: palette.primary.contrastText,
        boxShadow: 'none', // No shadow
        border: `1px solid ${palette.primary.main}`,
        fontFamily: typography.fontFamily,
        lineHeight: 1.75,
        fontWeight: 400,
        '&:hover': {
          backgroundColor: palette.primary.dark,
          boxShadow: 'none', // No shadow even on hover
          border: `1px solid ${palette.primary.dark}`,
        },
      },
      // Secondary contained button (gray)
      containedSecondary: {
        backgroundColor: palette.secondary.main,
        color: palette.secondary.contrastText,
        boxShadow: 'none',
        border: `1px solid ${palette.secondary.main}`,
        '&:hover': {
          backgroundColor: palette.secondary.dark,
          boxShadow: 'none',
          border: `1px solid ${palette.secondary.dark}`,
        },
      },
      // Outlined button
      outlined: {
        border: 'none',
        boxShadow: 'none',
        backgroundColor: 'rgba(0,0,0,0.04)',
        '&:hover': {
          border: 'none',
          boxShadow: 'none',
          backgroundColor: 'rgba(0,0,0,0.08)',
        },
      },
      // Small button
      sizeSmall: {
        padding: '0.375rem 1rem',
        fontSize: '1rem',
        height: '2.25rem',
      },
      // Large button
      sizeLarge: {
        padding: '0.625rem 2rem',
        fontSize: '1.5rem',
        height: '3.375rem',
      },
    },
  },
  // Card styling
  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: shape.borderRadius,
        boxShadow: shadows[2],
      },
    },
  },
  // Paper styling (used in multiple components)
  MuiPaper: {
    styleOverrides: {
      rounded: {
        borderRadius: shape.borderRadius,
      },
      elevation1: {
        boxShadow: shadows[1],
      },
      elevation2: {
        boxShadow: shadows[2],
      },
      elevation3: {
        boxShadow: shadows[3],
      },
    },
  },
  // App Bar styling
  MuiAppBar: {
    styleOverrides: {
      root: {
        boxShadow: shadows[2],
      },
    },
  },
  // TextField styling
  MuiTextField: {
    styleOverrides: {
      root: {
        '& .MuiOutlinedInput-root': {
          borderRadius: shape.borderRadius,
        },
        '& .MuiInputBase-input': {
          fontSize: '1.25rem', // 20px
          color: palette.text.primary,
          padding: '6.5px 14px',
        },
        // Default to single line size
        '&.single-line-field .MuiOutlinedInput-root': {
          width: '100%',
          height: '2.5rem',
        },
        // Multi-line field size
        '&.multi-line-field .MuiOutlinedInput-root': {
          width: '100%',
          minHeight: '120px',
        },
        // Comment field size
        '&.comment-field .MuiOutlinedInput-root': {
          width: '100%',
          height: '2.25rem',
        },
      },
    },
    variants: [
      {
        props: { size: 'singleLine' },
        style: {
          width: '100%',
          maxWidth: '100%',
          '& .MuiOutlinedInput-root': {
            height: '2.25rem',
            maxWidth: '100%',
          },
        },
      },
      {
        props: { size: 'multiLine' },
        style: {
          width: '100%',
          maxWidth: '100%',
          '& .MuiOutlinedInput-root': {
            height: 'auto',
            minHeight: '120px',
            maxWidth: '100%',
          },
        },
      },
      {
        props: { size: 'commentField' },
        style: {
          width: '100%',
          maxWidth: '100%',
          '& .MuiOutlinedInput-root': {
            height: '2.25rem',
            maxWidth: '100%',
          },
        },
      },
    ],
  },
  // Select styling
  MuiSelect: {
    styleOverrides: {
      root: {
        '& .MuiOutlinedInput-root': {
          borderRadius: shape.borderRadius,
        }
      },
      select: {
        padding: '6.5px 14px',
      }
    },
  },
  // OutlinedInput styling (used by TextField and Select)
  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        borderRadius: shape.borderRadius,
      }
    }
  },
  // Chip styling
  MuiChip: {
    styleOverrides: {
      root: {
        fontWeight: 400,
        borderRadius: shape.borderRadius,
      },
    },
  },
  // Table styling
  MuiTableHead: {
    styleOverrides: {
      root: {
        backgroundColor: palette.grey[100],
        '& .MuiTableCell-head': {
          fontSize: '1.125rem', // 18px
          fontStyle: 'italic',
          fontWeight: typography.fontWeightLight,
          color: palette.text.primary,
        },
      },
    },
  },
  MuiTableRow: {
    styleOverrides: {
      root: {
        '&.MuiTableRow-root:nth-of-type(even)': {
          backgroundColor: 'rgba(162, 171, 179, 0.46)', // #A2ABB3 with 46% opacity
        },
        '& .MuiTableCell-body': {
          fontSize: '1.125rem', // 18px
          fontWeight: typography.fontWeightLight,
          color: palette.text.primary,
        },
      },
    },
  },
  // Progress bar styling
  MuiLinearProgress: {
    styleOverrides: {
      root: {
        borderRadius: shape.borderRadius,
        backgroundColor: palette.grey[100],
        height: '6px',
      },
    },
  },
  // Dialog styling
  MuiDialog: {
    styleOverrides: {
      paper: {
        borderRadius: shape.borderRadiusMedium,
      },
    },
  },
  // Divider styling
  MuiDivider: {
    styleOverrides: {
      root: {
        borderColor: palette.divider,
      },
      fullWidth: {
        '&.css-1t9cf9z-MuiDivider-root': {
          border: '1px solid #ffffff',
        },
        '&.css-43n20n-MuiDivider-root': {
          margin: 0,
          flexShrink: 0,
          borderWidth: 0,
          borderStyle: 'solid',
          borderColor: '#ffffff',
          borderBottomWidth: '1px',
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
        },
      },
    },
  },
  // Typography styling
  MuiTypography: {
    styleOverrides: {
      root: {
        fontFamily: '"Sofia Pro Regular", "Sofia Pro", sans-serif',
        fontWeight: 400,
      },
      h1: {
        fontFamily: '"Sofia Pro Bold", "Sofia Pro", sans-serif !important',
        fontWeight: 700,
      },
      h2: {
        fontFamily: '"Sofia Pro Bold", "Sofia Pro", sans-serif !important',
        fontWeight: 700,
      },
      h3: {
        fontFamily: '"Sofia Pro Bold", "Sofia Pro", sans-serif !important',
        fontWeight: 700,
      },
      h4: {
        fontFamily: '"Sofia Pro Bold", "Sofia Pro", sans-serif !important',
        fontWeight: 700,
      },
      h5: {
        fontFamily: '"Sofia Pro Bold", "Sofia Pro", sans-serif !important',
        fontWeight: 700,
      },
      h6: {
        fontFamily: '"Sofia Pro Bold", "Sofia Pro", sans-serif !important',
        fontWeight: 700,
      },
      body1: {
        fontFamily: '"Sofia Pro Regular", "Sofia Pro", sans-serif !important',
        fontWeight: 400,
      },
      body2: {
        fontFamily: '"Sofia Pro Regular", "Sofia Pro", sans-serif !important',
        fontWeight: 400,
      },
      gutterBottom: {
        marginBottom: '0.5em',
      },
    },
  },
  // Icon button styling
  MuiIconButton: {
    styleOverrides: {
      root: {
        color: palette.secondary.main,
        '&:hover': {
          backgroundColor: 'rgba(72, 88, 105, 0.04)',
        },
      },
    },
  },
  // Form Label styling
  MuiFormLabel: {
    styleOverrides: {
      root: {
        fontFamily: '"Sofia Pro Bold", "Sofia Pro", sans-serif !important',
        fontWeight: 700,
        color: 'var(--color-blue)',
      },
    },
  },
  // Input Label styling
  MuiInputLabel: {
    styleOverrides: {
      root: {
        fontFamily: '"Sofia Pro Bold", "Sofia Pro", sans-serif !important',
        fontWeight: 700,
        color: 'var(--color-blue)',
      },
    },
  },
};

// ======================================================
// THEME CREATION
// Create the MUI theme using our design tokens and component styles
// ======================================================

/**
 * Create the theme
 */
const theme = createTheme({
  palette,
  typography,
  spacing,
  breakpoints: {
    values: breakpoints.values,
  },
  shape: {
    borderRadius: shape.borderRadius,
  },
  shadows,
  components,
});

/**
 * Design Tokens Export
 * Export our design tokens for use in styled components or direct styling
 */
export const tokens = {
  colors: palette,
  typography,
  spacing,
  breakpoints: breakpoints.values,
  shape,
  shadows,
};

/**
 * Helper Functions
 * Utility functions for working with the theme
 */
export const themeUtils = {
  /**
   * Get theme color with specific opacity
   * @param {string} colorPath - Path to color in palette (e.g., "primary.main")
   * @param {number} opacity - Opacity value between 0 and 1
   * @returns {string} - Color with opacity in rgba format
   */
  getColorWithOpacity: (colorPath, opacity) => {
    // This function needs to be used with the theme instance
    // Example usage: themeUtils.getColorWithOpacity(theme, "primary.main", 0.5)
    return (themeInstance) => {
      const color = colorPath.split('.').reduce((obj, key) => obj[key], themeInstance.palette);
      if (!color) return 'rgba(0,0,0,0)';
      
      // Convert hex to rgb
      let hex = color.replace('#', '');
      if (hex.length === 3) {
        hex = hex.split('').map(h => h + h).join('');
      }
      
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);
      
      return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    };
  },
  
  /**
   * Get responsive font size
   * @param {Object} options - Font size options for different breakpoints
   * @returns {Object} - CSS object with media queries
   */
  responsiveFontSize: (options) => {
    const { xs, sm, md, lg, xl } = options;
    return {
      fontSize: xs,
      [`@media (min-width:${breakpoints.values.sm}px)`]: {
        fontSize: sm || xs,
      },
      [`@media (min-width:${breakpoints.values.md}px)`]: {
        fontSize: md || sm || xs,
      },
      [`@media (min-width:${breakpoints.values.lg}px)`]: {
        fontSize: lg || md || sm || xs,
      },
      [`@media (min-width:${breakpoints.values.xl}px)`]: {
        fontSize: xl || lg || md || sm || xs,
      },
    };
  },
};

export default theme;